import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import { ContactUsMode } from '../components/forms/ContactForm';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import ParallaxLayoutList, { ParallaxLayoutTextImageCard } from '../components/layout/lists/ParallaxLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';
import FAQWidget from '../widgets/interactive/FAQWidget';
import UseCasesShowcase from '../widgets/showcases/UseCasesShowcase';

// Dynamically import with SSR disabled
const InstallationGuideShowcase = dynamic(() => import('../widgets/showcases/InstallationGuideShowcase'), { ssr: false });

type SlackLandingPageStaticProps = {};

export const getStaticProps: GetStaticProps<SlackLandingPageStaticProps> = async (context) => {
  const props: SlackLandingPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const SlackLandingPage: NextPageWithLayout<SlackLandingPageStaticProps> = (props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.LandingPageSlack);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Slack Integration"
        title="Generative AI helpdesk for employee support on Slack"
        description="Auto-resolve 40% of employee issues instantly with Gaspar AI’s chatbot Slack integration. Our Generative AI chatbot enables quick problem-solving through natural language. From password resets to time-off requests, our LLM-trained chatbot understands user intent and offers context-aware solutions, deflecting tickets and reducing manual work."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Gaspar AI chatbot Slack Integration Key Features" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('landing-page-slack-automated-resolution'),
              orientation: 'right',
              title: 'Automated resolution',
              description:
                'Gaspar AI’s Generative AI chatbot automatically and instantly resolves 40% of employee issues without human intervention, powered by LLMs and 3rd party plugins. Most common problems addressed include password resets, software provisioning, group and email management, and more!',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-slack-automated-approvals'),
              orientation: 'left',
              title: 'Automated approvals',
              description:
                "Make it easy for everyone to ask for time-off or access the channels, folders and groups they need but not the ones they don't. The process happens on Slack for both the requester and the approver, and they can track the request’s status on the same platform.  ",
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-slack-instant-accurate-human-like-answers'),
              orientation: 'right',
              title: 'Instant, accurate, human-like answers',
              description:
                'Your workforce can instantly find information – HR policies, IT troubleshooting and more just by asking our AI Virtual Agent. It searches articles, documents and data across knowledge bases and repositories, and can ask follow-up questions until employees get the exact answer.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-slack-no-code-workflow-automation'),
              orientation: 'left',
              title: 'No code workflow automation',
              description:
                'Reduce manual work by 80% and minimise repetitive tasks such as onboarding by creating no-code, customised workflows. You can activate them whenever needed directly from Slack or set up triggers.',
            },
            {
              imageUrl: CDNAssets.getUrl('landing-page-slack-hassle-free-automated-ticketing'),
              orientation: 'right',
              title: 'Hassle-free, automated ticketing',
              description:
                "In case of auto-resolution, a ticket is automatically created on your ITSM platform. If the chatbot can't resolve the issue, it facilitates handoff to the right agent with easy ticket creation and smart routing.",
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <InstallationGuideShowcase
          description={`Follow these simple steps to install Gaspar AI and boost your company's efficiency and productivity.`}
          records={[
            { title: 'Start your free trial', description: 'Fill in the form and get 21 days free.' },
            {
              title: 'Log in with SSO',
              description: 'Once we send you the email with your instance you must log in with your SSO (e.g. Google account)',
            },
            { title: 'Enable your SSO', description: 'Go to the Admin Hub/integrations and enable your SSO (e.g. Office 365)' },
            { title: 'Enable ticketing', description: 'Enable your ticketing system (e.g. Atlassian) and configure it' },
            { title: 'Enable Slack', description: 'Go to the Admin Hub/integrations and enable Slack' },
            { title: 'Invite Gaspar', description: 'Invite and add our AI chatbot to the channels you want it to respond to queries' },
            { title: 'Choose channel settings', description: 'Choose when Gaspar will respond to messages' },
            { title: 'Choose Emojis', description: 'Choose which emojis to use to manage tickets' },
            { title: 'Enable automations', description: 'Select the automations you wish to have' },
          ]}
        />
      </PageSection>
      <PageSection>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={false} />
      </PageSection>
      <PageSection>
        <UseCasesShowcase
          orientation="right"
          title="Service Desk and employee benefits"
          descriptionList={[
            '40% of issues automatically resolved',
            '80% less manual work',
            'Increased productivity',
            '24/7 access to help',
            'More time to work on strategic, impactful projects ',
          ]}
          imageUrl={CDNAssets.getUrl('use-cases-page-self-service')}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle mode="parallax" text="How does it work?" />
        <ParallaxLayoutList
          renderItem={(item) => <ParallaxLayoutTextImageCard {...item} />}
          items={[
            {
              title: 'Start the conversation!',
              data: {
                description:
                  'Click either on “Get Help” or go to “Messages” and type your question or go to the IT public channel and ask your question.',
                imageUrl: CDNAssets.getUrl('landing-page-slack-start-the-conversation'),
              },
            },
            {
              title: 'Query solved within a few seconds! ',
              data: {
                description: 'Gaspar will instantly answer your question or ask follow-up questions until you get an accurate answer.',
                imageUrl: CDNAssets.getUrl('landing-page-slack-question-solved'),
              },
            },
            {
              title: 'Ticket creation on Slack',
              data: {
                description: 'If the question can’t be answered by Gaspar, then you simply fill in a form on Slack to create a ticket.',
                imageUrl: CDNAssets.getUrl('landing-page-slack-ticket-creation'),
              },
            },
            {
              title: '"Home" Tab',
              data: {
                description:
                  'In this tab you can see all your open tickets, any pending approval requests, open new tickets and submit any help query. No need to switch between apps!',
                imageUrl: CDNAssets.getUrl('landing-page-slack-home-tab'),
              },
            },
            {
              title: '"Message" Tab',
              data: {
                description:
                  'Here the helpdesk agents can see their tickets’ status, daily statistics on the tickets handled by you and SLAs.',
                imageUrl: CDNAssets.getUrl('landing-page-slack-message-tab'),
                paragraphs: [],
              },
            },
            {
              title: '"About" Tab',
              data: {
                description: 'In this section you can see all your shortcuts.',
                imageUrl: CDNAssets.getUrl('landing-page-slack-about-tab'),
                paragraphs: [],
              },
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
SlackLandingPage.displayName = 'SlackLandingPage';
export default SlackLandingPage;
